import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { Plus, X, Save, Trash2 } from "lucide-react";

export default function ModalCalendarWeek({ initialSchedule, onSave, onClose, isModalOpen, setIsModalOpen, disabledDays, errorMessageOverLap }) {
  const [schedule, setSchedule] = useState(initialSchedule);

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const isOverlapping = (block1, block2) => {
    const start1 = timeToMinutes(block1.start);
    const end1 = timeToMinutes(block1.end);
    const start2 = timeToMinutes(block2.start);
    const end2 = timeToMinutes(block2.end);

    return start1 < end2 && end1 > start2;
  };

  const handleTimeChange = (day, index, field, value) => {
    if (disabledDays.includes(day)) return; 

    const updatedSchedule = { ...schedule };
    const updatedBlock = { ...updatedSchedule[day][index], [field]: value };

    const isOverlap = updatedSchedule[day].some((block, i) => {
      if (i === index) return false; 
      return isOverlapping(block, updatedBlock);
    });

    if (isOverlap) {
      errorMessageOverLap()
      return;
    }

    updatedSchedule[day][index] = updatedBlock;
    setSchedule(updatedSchedule);
  };

  const addTimeBlock = (day) => {
    if (disabledDays.includes(day)) return; 

    const lastBlock = schedule[day][schedule[day].length - 1]; 
    const defaultStart = lastBlock ? lastBlock.end : "09:00"; 
    const defaultEnd = "17:00"; 

    const newBlock = { start: defaultStart, end: defaultEnd };

    const isOverlap = schedule[day].some((block) => {
      return isOverlapping(block, newBlock);
    });

    if (isOverlap) {
      errorMessageOverLap()
      return;
    }

    const updatedSchedule = { ...schedule };
    updatedSchedule[day].push(newBlock);
    setSchedule(updatedSchedule);
  };

  const removeTimeBlock = (day, index) => {
    if (disabledDays.includes(day)) return; 

    const updatedSchedule = { ...schedule };
    updatedSchedule[day].splice(index, 1);
    setSchedule(updatedSchedule);
  };

  const toggleDayAvailability = (day) => {
    if (disabledDays.includes(day)) return; 

    const updatedSchedule = { ...schedule };
    if (updatedSchedule[day].length === 0) {
      updatedSchedule[day] = [{ start: "09:00", end: "17:00" }];
    } else {
      updatedSchedule[day] = [];
    }
    setSchedule(updatedSchedule);
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          key="modal-background"
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-20 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            key="modal-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="bg-background rounded-lg shadow-xl p-6 font-sans w-full max-w-lg mx-4 border border-gray-200 flex flex-col max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-xl font-semibold mb-4 text-text">Edit Recurring Weekly Hours</h2>

            <div className="space-y-3">
              {Object.entries(schedule).map(([day, timeBlocks]) => (
                <div key={day} className="border-b border-gray-100 pb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-subtitle">{day.toUpperCase()}</span>
                    {!disabledDays.includes(day) && ( 
                      <button
                        onClick={() => addTimeBlock(day)}
                        className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded-full"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {timeBlocks.length > 0 ? (
                    timeBlocks.map((block, index) => (
                      <div key={index} className="flex items-center gap-2 mb-2">
                        <input
                          type="time"
                          value={block.start}
                          onChange={(e) => handleTimeChange(day, index, "start", e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
                          disabled={disabledDays.includes(day)} 
                        />
                        <span className="text-gray-400">-</span>
                        <input
                          type="time"
                          value={block.end}
                          onChange={(e) => handleTimeChange(day, index, "end", e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 w-full text-sm"
                          disabled={disabledDays.includes(day)} 
                        />
                        {!disabledDays.includes(day) && ( 
                          <button
                            onClick={() => removeTimeBlock(day, index)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">Unavailable</span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors w-full sm:w-auto flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={() => onSave(schedule)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-full sm:w-auto flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}